import React, { useState } from "react";
import {
  Search,
  Filter,
  LayoutGrid,
  Calendar as CalendarIcon,
  Star,
  Heart,
  Compass,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import EventCard from "../../components/evenements/EventCard";
import EventDetailView from "../../components/evenements/EventDetailView";
import OperationalCalendar from "../../components/evenements/OperationalCalendar";
import { Event, EventStatus, EventFormat } from "../../types/event.d";
import { cn } from "../../lib/utils";

const MOCK_EVENTS: Event[] = [
  {
    id: "EVT-2023-001",
    title: "Conférence sur l'Écologie Urbaine 2023",
    description:
      "Une session intensive sur les nouvelles technologies de gestion des déchets dans les zones urbaines denses du Cameroun.",
    date: "2023-10-12T09:00:00Z",
    location: "Douala, CM",
    format: EventFormat.PHYSICAL,
    status: EventStatus.COMPLETED,
    category: "Environnement",
    documents: [
      {
        id: "doc1",
        name: "Plan de gestion des déchets.pdf",
        url: "#",
        type: "pdf",
        size: "1.2 MB",
      },
      {
        id: "doc2",
        name: "Guide de recyclage Urbain.pdf",
        url: "#",
        type: "pdf",
        size: "2.5 MB",
      },
      {
        id: "doc3",
        name: "Étude de cas Douala.pdf",
        url: "#",
        type: "pdf",
        size: "0.8 MB",
      },
    ],
    speakers: [
      { id: "s1", name: "Dr. Sarah Jenkins", role: "Chief Scientist" },
      { id: "s2", name: "Marcus Chen", role: "Founder" },
    ],
  },
  {
    id: "EVT-2024-042",
    title: "Webinaire: IA et Agriculture Durable",
    description:
      "Découvrez comment l'intelligence artificielle peut optimiser les rendements agricoles tout en préservant la biodiversité.",
    date: "2024-11-24T14:00:00Z",
    location: "En ligne",
    format: EventFormat.ONLINE,
    status: EventStatus.CONFIRMED,
    category: "Technologie",
    price: 0,
    currency: "XAF",
  },
  {
    id: "EVT-2024-015",
    title: "Atelier: Financement de Projets Verts",
    description:
      "Comment structurer son dossier pour obtenir des subventions internationales ? Retours d'expérience et études de cas.",
    date: "2024-01-05T10:00:00Z",
    location: "En ligne / App",
    format: EventFormat.ONLINE,
    status: EventStatus.PRESENCE_VALIDATED,
    category: "Financement",
  },
  {
    id: "EVT-2024-088",
    title: "Networking: Jeunes Entrepreneurs de Douala",
    description:
      "Une soirée de réseautage pour connecter les porteurs de projets avec des mentors et investisseurs locaux.",
    date: "2024-12-15T18:30:00Z",
    location: "Douala Grand Mall",
    format: EventFormat.PHYSICAL,
    status: EventStatus.PENDING,
    category: "Networking",
  },
  {
    id: "EVT-2024-102",
    title: "Atelier: Pitch Deck & Storytelling",
    description:
      "Apprenez à captiver votre audience en présentant votre projet de manière percutante.",
    date: "2024-12-01T09:00:00Z",
    location: "En ligne",
    format: EventFormat.ONLINE,
    status: EventStatus.PENDING,
    category: "Soft Skills",
  },
  {
    id: "EVT-2024-115",
    title: "Conférence: Les Enjeux de la Fintech en Afrique",
    description:
      "Panorama des opportunités et des régulations pour les startups financières sur le continent.",
    date: "2024-11-30T10:00:00Z",
    location: "Yaoundé, CM",
    format: EventFormat.PHYSICAL,
    status: EventStatus.PENDING,
    category: "Fintech",
  },
];

const Evenements: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "inscriptions" | "exploration" | "favoris"
  >("inscriptions");
  const [viewMode, setViewMode] = useState<"gallery" | "calendar">("gallery");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("Tous");

  const categories = [
    "Tous",
    "Technologie",
    "Environnement",
    "Financement",
    "Networking",
    "Soft Skills",
    "Fintech",
  ];

  const toggleFavorite = (eventId: string) => {
    setFavoriteIds((prev: string[]) =>
      prev.includes(eventId)
        ? prev.filter((id: string) => id !== eventId)
        : [...prev, eventId],
    );
  };

  const tabs = [
    { id: "inscriptions", label: "Mes Inscriptions", icon: LayoutGrid },
    { id: "exploration", label: "Exploration", icon: Search },
    { id: "favoris", label: "Favoris", icon: Star },
  ];

  const filteredEvents = MOCK_EVENTS.filter((event) => {
    const matchesSearch =
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.id.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      activeCategory === "Tous" || event.category === activeCategory;

    if (activeTab === "inscriptions") {
      return (
        matchesSearch &&
        (event.status === EventStatus.CONFIRMED ||
          event.status === EventStatus.PRESENCE_VALIDATED ||
          event.status === EventStatus.COMPLETED)
      );
    }

    if (activeTab === "favoris") {
      return matchesSearch && favoriteIds.includes(event.id);
    }

    if (activeTab === "exploration") {
      return matchesSearch && matchesCategory;
    }

    return matchesSearch;
  });

  return (
    <div className="flex flex-col min-h-screen bg-slate-50/50 relative">
      {/* Header section */}
      <div className="bg-white border-b border-slate-200 px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-rajapiBlue p-2 rounded-lg text-white">
              <CalendarIcon size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Mes Évènements
              </h1>
              <p className="text-sm text-slate-500">
                Gérez vos participations et soumettez vos feedbacks.
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() =>
                setActiveTab(
                  tab.id as "inscriptions" | "exploration" | "favoris",
                )
              }
              className={`pb-4 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? "text-rajapiBlue"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-rajapiBlue rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar / Search Section */}
      <div className="px-8 py-6 flex flex-col gap-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <Input
              placeholder="Rechercher un événement..."
              className=" bg-white border-slate-200 focus:ring-rajapiBlue py-2 w-full rounded-md border"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="flex items-center gap-2 border-slate-200"
            >
              <Filter size={18} />
              <span>Filtres</span>
            </Button>
            <div className="flex items-center border border-slate-200 rounded-lg p-1 bg-white">
              <Button
                variant={viewMode === "gallery" ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "p-2 h-8 w-8",
                  viewMode === "gallery" && "bg-slate-100",
                )}
                onClick={() => setViewMode("gallery")}
              >
                <LayoutGrid size={18} />
              </Button>
              <Button
                variant={viewMode === "calendar" ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "p-2 h-8 w-8",
                  viewMode === "calendar" && "bg-slate-100",
                )}
                onClick={() => setViewMode("calendar")}
              >
                <CalendarIcon size={18} />
              </Button>
            </div>
          </div>
        </div>

        {activeTab === "exploration" && (
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  "px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border",
                  activeCategory === cat
                    ? "bg-rajapiBlue text-white border-rajapiBlue shadow-sm"
                    : "bg-white text-slate-500 border-slate-200 hover:border-rajapiBlue hover:text-rajapiBlue",
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Event Content Area */}
      <div className="px-8 pb-12 flex-1">
        {viewMode === "gallery" ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {filteredEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                isFavorite={favoriteIds.includes(event.id)}
                onToggleFavorite={() => toggleFavorite(event.id)}
                onViewDetails={(ev) => setSelectedEvent(ev)}
                onAction={(ev) => {
                  if (ev.status === EventStatus.PENDING) {
                    alert(`Inscription réussie pour: ${ev.title}`);
                  } else if (ev.status === EventStatus.CONFIRMED) {
                    alert(`Inscription annulée pour: ${ev.title}`);
                  } else {
                    alert(`Action déclenchée pour: ${ev.title}`);
                  }
                }}
              />
            ))}
          </div>
        ) : (
          <div className="h-[calc(100vh-320px)] min-h-[600px]">
            <OperationalCalendar
              events={filteredEvents}
              onEventClick={(ev: Event) => setSelectedEvent(ev)}
            />
          </div>
        )}

        {viewMode === "gallery" && filteredEvents.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-white rounded-3xl border border-dashed border-slate-200 max-w-3xl mx-auto shadow-sm">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-rajapiBlue/10 rounded-full scale-150 blur-xl animate-pulse" />
              <div className="relative bg-white p-4 rounded-full shadow-lg">
                {activeTab === "favoris" ? (
                  <Heart className="text-red-400" size={48} />
                ) : activeTab === "exploration" ? (
                  <Compass className="text-rajapiBlue" size={48} />
                ) : (
                  <CalendarIcon className="text-slate-400" size={48} />
                )}
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">
              {activeTab === "favoris"
                ? "Aucun favori pour le moment"
                : activeTab === "exploration"
                  ? "Aucun événement trouvé"
                  : "Aucune inscription"}
            </h3>
            <p className="text-slate-500 mb-8 max-w-sm text-center">
              {activeTab === "favoris"
                ? "Ajoutez des événements à vos favoris pour les retrouver facilement plus tard."
                : activeTab === "exploration"
                  ? "Essayez de modifier vos filtres ou votre recherche pour découvrir d'autres opportunités."
                  : "Vous n'avez pas encore d'événements validés ou à venir."}
            </p>
            {activeTab !== "exploration" && (
              <Button
                onClick={() => setActiveTab("exploration")}
                className="bg-rajapiBlue text-white hover:bg-rajapiNavyBlue gap-2 px-6"
              >
                <Search size={18} />
                Explorer les événements
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Detail Overlay */}
      {selectedEvent && (
        <div 
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-center items-center p-4 md:p-10 animate-in fade-in duration-200"
          onClick={() => setSelectedEvent(null)}
        >
          <div 
            className="w-full max-w-6xl h-full bg-white rounded-3xl shadow-2xl overflow-hidden relative border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <EventDetailView
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
              onRegister={() => {
                // Future API call: register(ev.id)
                setSelectedEvent(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Evenements;
